import { useEffect, useState } from "react";
import { Loader2, FileText, CheckCircle, XCircle, Clock } from "lucide-react";
import { getInsuranceMySubmittedQuotes } from "@/Calls/callInsuranceAuth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface Quote {
    quote_id: number;
    request_id: number;
    coverage_type: string | null;
    premium_monthly: string | null;
    premium_annual: string | null;
    deductible: string | null;
    binder_number: string | null;
    binder_url: string | null;
    status: string;
    created_at: string;
    request: {
        vehicle_year: number | null;
        vehicle_make: string | null;
        vehicle_model: string | null;
        vehicle_vin: string | null;
    };
}

function StatusBadge({ status }: { status: string }) {
    if (status === "ACCEPTED") return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle className="h-3 w-3 mr-1" />Accepted</Badge>;
    if (status === "DECLINED") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" />Declined</Badge>;
    return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
}

export default function InsuranceMyQuotesPage() {
    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getInsuranceMySubmittedQuotes()
            .then((d) => setQuotes(d.quotes ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex items-center justify-center min-h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">My Submitted Quotes</h1>
                <p className="text-muted-foreground text-sm mt-1">{quotes.length} quote{quotes.length !== 1 ? "s" : ""} submitted</p>
            </div>

            {quotes.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No quotes submitted yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Pull leads and submit quotes to see them here.</p>
                </Card>
            ) : (
                <div className="space-y-3">
                    {quotes.map((q) => (
                        <Card key={q.quote_id} className="p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <p className="font-medium">
                                        {q.request.vehicle_year} {q.request.vehicle_make} {q.request.vehicle_model}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {q.coverage_type ?? "—"}
                                        {q.premium_monthly ? ` · $${q.premium_monthly}/mo` : ""}
                                        {q.deductible ? ` · $${q.deductible} deductible` : ""}
                                    </p>
                                    {q.binder_number && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Binder: {q.binder_number}
                                            {q.binder_url && (
                                                <a
                                                    href={q.binder_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="ml-2 text-blue-600 underline"
                                                >
                                                    View PDF
                                                </a>
                                            )}
                                        </p>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Submitted {new Date(q.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <StatusBadge status={q.status} />
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
